// eslint-disable-next-line import/no-cycle
import type { EntitiesEntity } from "app/features/Location/types/YextLocationResponseType";
import type { RootState } from "app/store";
import { getKey } from "app/shared/utils/EncryptionUtility";
import { DateTime } from "luxon";
import type {
    CreateUpdateOrderRequest,
    ModifierGroupCustomization,
    OrderItem,
    PickupTypeKey,
    StoreMenu,
    TOrderState,
    Upsell,
} from "./tOrderTypes";

type DefaultPickupType = Pick<TOrderState, "pickupType" | "diningOption">;
type ProcessedStoreInformation = Pick<TOrderState, "storeId" | "pickupTypeMap">
    & Partial<DefaultPickupType>;
type OrderPayload = CreateUpdateOrderRequest & Pick<TOrderState, "storeId" | "orderId">;

const normalizePickupType = (pickupType: string): PickupTypeKey => (
    pickupType.replace('-', '_') as PickupTypeKey
);

export const getUserSelectedModifierMap = (
    modifiers: OrderItem["modifiers"],
) => (modifiers.filter((modifier) => modifier.isUserSelectedOption).map((modifier) => `${modifier.modifierGroupId}-${modifier.optionId}`));

const getPriceInCents = (price: number) => Math.round(price * 100);

function isSameMenuAndUpsellItem(newItem: OrderItem, existingItem: OrderItem) {
    const [upsellItem, menuItem] = newItem.modifiers.length <= existingItem.modifiers.length
        ? [newItem, existingItem]
        : [existingItem, newItem];

    if (!menuItem.hasDefaultNonSizeSelections) return false;
    if (
        getPriceInCents(upsellItem.price || 0)
            !== getPriceInCents(menuItem.price || 0)) return false;
    const upsellModifierMap = getUserSelectedModifierMap(upsellItem.modifiers);
    const upsellSize = upsellItem.modifiers.find(
        ({ isUserSelectedOption }) => isUserSelectedOption,
    );
    const hasSingleSizeSelection = upsellModifierMap.length <= 1
        && upsellSize?.modifierGroupName?.trim().toLowerCase() === "size";
    const hasValidSize = hasSingleSizeSelection
        && Boolean(upsellSize.modifierGroupId.trim() && upsellSize.optionId.trim());
    if (!hasValidSize) return false;

    const menuModifierMap = getUserSelectedModifierMap(menuItem.modifiers);

    // An upsell without modifier identifiers cannot be safely matched to a menu variant.
    if (upsellModifierMap.length === 0) return false;

    return upsellModifierMap.every(
        (modifierKey) => {
            const menuModifierIndex = menuModifierMap.indexOf(modifierKey);
            if (menuModifierIndex !== -1) {
                menuModifierMap.splice(menuModifierIndex, 1);
                return true;
            }
            return false;
        },
    );
}

export function hasOnlyDefaultNonSizeSelections(
    currentCustomizations: ModifierGroupCustomization[],
    initialCustomizations: ModifierGroupCustomization[],
): boolean {
    const isSizeSelection = ({ modifierGroupName }: ModifierGroupCustomization) => (
        modifierGroupName.trim().toLowerCase().includes("size")
    );
    const currentNonSizeSelections = currentCustomizations.filter(
        (customization) => !isSizeSelection(customization),
    );
    const initialNonSizeSelections = initialCustomizations.filter(
        (customization) => !isSizeSelection(customization),
    );

    if (currentNonSizeSelections.length !== initialNonSizeSelections.length) return false;

    const initialOptionByGroup = new Map(
        initialNonSizeSelections.map(({ modifierGroupId, optionSelected }) => (
            [modifierGroupId, optionSelected.multiLocationId]
        )),
    );

    return currentNonSizeSelections.every(({ modifierGroupId, optionSelected }) => (
        initialOptionByGroup.get(modifierGroupId) === optionSelected.multiLocationId
    ));
}

export function checkIfOrderItemExists(
    newItem: OrderItem,
    cartItems: OrderItem[],
): string | null {
    const newItemModifierMap = getUserSelectedModifierMap(newItem.modifiers);
    // Check every customization of the menu item, not just the first cart entry.
    const existingCartItem = cartItems.find((cartItem) => {
        if (cartItem.itemId !== newItem.itemId) return false;
        if (isSameMenuAndUpsellItem(newItem, cartItem)) return true;

        if (getPriceInCents(newItem.price || 0)
            !== getPriceInCents(cartItem.price || 0)) return false;
        if (getUserSelectedModifierMap(cartItem.modifiers).length !== newItemModifierMap.length) {
            return false;
        }
        const existingModifierMap = getUserSelectedModifierMap(cartItem.modifiers);
        return newItemModifierMap.every(
            (modifierKey) => {
                const existingModifierIndex = existingModifierMap.indexOf(modifierKey);
                if (existingModifierIndex !== -1) {
                    existingModifierMap.splice(existingModifierIndex, 1);
                    return true;
                }
                return false;
            },
        );
    });

    return existingCartItem?.itemUniqueId || null;
}

function getModifierMap(modifiers: OrderItem["modifiers"]) {
    return modifiers.filter((modifier) => !!modifier.optionId)
        .map((modifier) => (`${modifier.modifierGroupId}-${modifier.optionId}`));
}

export function getItemUniqueIdByItem(newItem: OrderItem, cartItems: OrderItem[]) {
    const newItemModifierMap = getModifierMap(newItem.modifiers);
    const existingCartItem = cartItems.find((cartItem) => {
        if (cartItem.itemId !== newItem.itemId) return false;
        const existingItemModifierMap = getModifierMap(cartItem.modifiers);
        const tempNewItemModifierMap = [...newItemModifierMap];
        return (existingItemModifierMap.length === newItemModifierMap.length)
               && existingItemModifierMap.every(
            (modifierKey) => {
                const newItemModifierIndex = tempNewItemModifierMap.indexOf(modifierKey);
                if (newItemModifierIndex !== -1) {
                    tempNewItemModifierMap.splice(newItemModifierIndex, 1);
                    return true;
                }
                return false;
            },
        );
    });
    return existingCartItem?.itemUniqueId || null;
}

export function getProcessedStoreInformation(
    store: EntitiesEntity | null,
): ProcessedStoreInformation {
    if (!store) {
        return {
            storeId: "",
            pickupTypeMap: {},
            pickupType: "",
            diningOption: "",
        };
    }

    // Toast stores dining options as "PICKUP_TYPE|DINING_OPTION_ID" strings from Yext.
    const pickupTypeMap = store.c_toastDiningOptions?.reduce<
        Partial<Record<PickupTypeKey, string>>
    >(
        (accumulator, diningOption) => {
            const [orderTypeKey, orderTypeId] = diningOption.split('|');

            accumulator[normalizePickupType(orderTypeKey)] = orderTypeId;
            return accumulator;
        },
        {},
    ) ?? {};

    return {
        storeId: store.c_toastRestaurantId || "",
        pickupTypeMap,
    };
}

export function getDefaultPickupType(
    pickupTypeMap: Partial<Record<PickupTypeKey, string>>,
    selectedPickupType: string | null,
): DefaultPickupType {
    if (!selectedPickupType) {
        return {
            pickupType: "",
            diningOption: "",
        };
    }

    const normalizedPickupType = normalizePickupType(selectedPickupType);
    const pickupType = normalizedPickupType;

    return {
        pickupType,
        diningOption: pickupTypeMap[normalizedPickupType] || "",
    };
}

export function calculateTotal(lineItems: OrderItem[]): string {
    return lineItems
        .reduce((total, lineItem) => total + ((lineItem.price || 0) * lineItem.quantity), 0)
        .toFixed(2);
}

export function calculateTotalCount(lineItems: OrderItem[]): number {
    return lineItems.reduce((total, lineItem) => total + lineItem.quantity, 0);
}

export function getAvailableUpsells(upsells: Upsell[]): Upsell[] {
    return upsells.filter(({ isAvailable }) => isAvailable);
}

const getFirstAvailableImage = (images: (string | null | undefined)[]): string => (
    images.find((image): image is string => (
        typeof image === "string" && Boolean(image.trim())
    )) || ""
);

export function getUpsellImage(upsell: Upsell, menu: StoreMenu | null): string {
    const apiImage = getFirstAvailableImage([upsell.thumbnail, upsell.image]);
    if (apiImage) return apiImage;

    const matchingMenuItem = menu?.categories
        .flatMap(({ menuItems }) => menuItems)
        .find(({ multiLocationId }) => multiLocationId === upsell.multiLocationId);
    const matchingMenuUpsell = menu?.upsells
        ?.find(({ multiLocationId }) => multiLocationId === upsell.multiLocationId);

    return getFirstAvailableImage([
        matchingMenuItem?.thumbnail,
        matchingMenuItem?.image,
        matchingMenuUpsell?.thumbnail,
        matchingMenuUpsell?.image,
    ]);
}

export function mapUpsellToOrderItem(
    upsell: Upsell,
    menuGroupMultiLocationId: string,
): OrderItem {
    const {
        modifierGroupMultiLocationId,
        modifierOptionMultiLocationId,
    } = upsell;
    const hasPreselectedModifier = Boolean(
        modifierGroupMultiLocationId && modifierOptionMultiLocationId,
    );

    return {
        itemId: upsell.multiLocationId,
        itemName: upsell.name,
        itemGroupId: menuGroupMultiLocationId,
        itemCategoryName: "",
        quantity: 1,
        price: upsell.price,
        imageThumb: upsell.thumbnail || upsell.image || "",
        hasDefaultNonSizeSelections: true,
        isBulk: false,
        modifiers: hasPreselectedModifier ? [{
            modifierGroupId: modifierGroupMultiLocationId || "",
            modifierGroupName: upsell.size ? "Size" : undefined,
            optionId: modifierOptionMultiLocationId || "",
            optionName: upsell.size || "",
            quantity: 1,
            isUserSelectedOption: true,
        }] : [],
    };
}

export function getOrderPayload(
    state: RootState,
    isPlacementRequest = false,
): OrderPayload {
    const { selectedStore, storeNumber } = state.stores;
    const address = selectedStore?.address;
    const timezone = selectedStore?.timezone;
    const {
        pickupType,
        diningOption,
        storeId,
        orderLines,
        orderId,
    } = state.tOrders;

    const store = {
        address: {
            zip: address?.postalCode || "",
            state: address?.region || "",
            city: address?.city || "",
            street1: address?.line1 || "",
        },
        timeZone: timezone || "",
        mainPhone: selectedStore?.mainPhone || "",
        storeId: storeNumber || "",
        storeName: selectedStore?.c_storeDescription || "",
        isCorporate: true,
    };
    const {
        firstName = "",
        lastName = "",
        email = "",
        phoneNumber: phone = "",
    } = state.userInfo;
    const { isGuest } = state.auth;
    const customer = !isGuest ? {
        firstName,
        lastName,
        email,
        phone,
    } : undefined;

    // UI-only cart fields are removed before sending the Toast create/update payload.
    return {
    pickupType,
    diningOption,
    store,
    customer,
    storeId,
    orderId,
    orderLines: orderLines.map(({
        order,
        modifiers,
        imageThumb,
        total,
        itemUniqueId,
        itemCategoryName,
        isUpsell,
        hasDefaultNonSizeSelections,
        isBulk,
        order: itemOrder,
        ...lineItem
      }) => ({
        modifiers: modifiers.filter(({optionId}) => optionId?.trim())
                    .map(({
                        isUserSelectedOption,
                        modifierGroupName,
                        bulkModifierGroupSetId,
                        id,
                        order: modifierOrder,
                        ...modifier
                    }) => ({
                        ...modifier,
                        price: isPlacementRequest ? modifierOrder?.price || 0 : undefined,
                    })),
        ...lineItem,
        price: isPlacementRequest ? order?.price || 0 : undefined,
        })),
    };
}

export const shouldPreferIncomingCartItem = (
    existingCartItem: OrderItem,
    incomingCartItem: OrderItem,
): boolean => (
    incomingCartItem.modifiers.length > existingCartItem.modifiers.length
        || (!existingCartItem.itemCategoryName && Boolean(incomingCartItem.itemCategoryName))
);

export function constructItemWithUpdatedQty(
    cartItem: OrderItem,
    updatedQuantity: number,
): OrderItem {
    const modifiersWithUpdatedQuantity = cartItem.modifiers.map(
        (modifier) => ({ ...modifier, quantity: updatedQuantity }),
    );

    return {
        ...cartItem,
        quantity: updatedQuantity,
        modifiers: modifiersWithUpdatedQuantity,
        total: (cartItem.price || 0) * updatedQuantity,
    };
}

export function getDefaultInitialState(): TOrderState {
    // Hydrate Toast order context from persisted store/pickup selections on app startup.
    const selectedStore = getKey('selectedStore');
    const sessionStoreEntity = selectedStore
        ? JSON.parse(selectedStore) as EntitiesEntity
        : null;
    const defaultStoreInformation = getProcessedStoreInformation(sessionStoreEntity);
    const sessionPickupType = getKey('pickUpType') || "";
    const defaultPickupType = getDefaultPickupType(
        defaultStoreInformation.pickupTypeMap,
        sessionPickupType,
    );
    return {
        hasRewardSelectedFromPage: false,
        isShellRewardInitialized: false,
        isShellAppInitialized: false,
        selectedCategoryName: null,
        cartItemSelectedForEdit: null,
        wallets: [],
        menu: null,
        lineItemMap: {},
        orderId: "",
        total: "",
        isASAP: true,
        isFutureOrder: false,
        tabName: "",
        payment: {
            fiserv: {
                clientToken: "",
                nonceToken: "",
                fundingSourceType: "",
            },
            chargeAmount: "",
        },
        summary: {
            subtotal: 0,
            tax: 0,
            total: 0,
        },
        serviceCharges: [],
        orderLines: [],
        totalCartItems: 0,
         ...defaultPickupType,
        ...defaultStoreInformation,
    };
}

export function getSelectedRewardValidity(state:RootState) {
    const selectedRewards = state.rewards.selectedReward;
    const {
        pickUpDate: orderPickupDate,
        timezone: storeTimezone,
    } = state.stores;
    if (!selectedRewards?.rewardId) {
        return false;
    }
    const pickupDate = DateTime.fromFormat(orderPickupDate, "yyyy-MM-dd HH:mm:ss 'UTC'", { zone: storeTimezone }).endOf('day');
    const selectedRewardExpiryDate = DateTime.fromFormat(
            selectedRewards.expireOn,
            'MMM. d, yyyy',
            {zone: storeTimezone},
        ).endOf('day');
    return pickupDate <= selectedRewardExpiryDate;
}

export function getSelectedRewards(state:RootState) {
    if (!getSelectedRewardValidity(state)) {
        return null;
    }
    const selectedRewards = state.rewards.selectedReward;
    const storeWalletRewards = state.tOrders.wallets;
    const selectedApplicableReward = storeWalletRewards.filter(
        (wallet) => wallet.walletCode === selectedRewards.rewardId,
    );
    const applicableRewardItems = selectedApplicableReward[0]?.categoryItemCodes.map(
        (categoryItem) => categoryItem.categoryCode,
    ) || [];
    const discountItems = state.tOrders.orderLines.filter(
        (lineItem) => applicableRewardItems.includes(lineItem.itemId),
    );
    return {
        walletCode: selectedRewards.rewardId,
        rewardName: selectedApplicableReward[0]?.walletName || "",
        discountAmount: selectedApplicableReward[0]?.valueAmount || "",
        discountMessage: selectedRewards.discountMessage,
        discountItem: discountItems[0]?.itemId || "",
    };
}

export function getOrderPayloadForPlacement(state:RootState) {
    const { wallets = [], appliedReward } = state.tOrders;
    const placementPayload = getOrderPayload(state, !!appliedReward);
    const selectedRewardWallet = wallets.find(
        (wallet) => wallet.walletCode === appliedReward?.walletCode,
    );
    let chosenReward;
    if (selectedRewardWallet) {
        chosenReward = {
            walletCode: String(selectedRewardWallet.walletCode),
            rewardName: selectedRewardWallet.walletName,
            discountMessage: appliedReward?.discountMessage,
        };
    }
    return {
        ...placementPayload,
        chosenReward,
    };
}
